import type { TreeMapData } from '@revelio/replots';

export type NestableTreeNode = {
  id: string | number;
  name: string;
  depth?: number;
  children?: readonly NestableTreeNode[];
};

// Taxonomy ids may be compound (`role_3_123`) while breakdown buckets carry the
// trailing numeric id, so both sides are keyed by that trailing segment.
export function nestedTreemapLookupKey(id: string | number): string {
  const raw = String(id);
  const tail = raw.includes('_') ? (raw.split('_').pop() ?? raw) : raw;
  return tail;
}

export type NestedTreemapParent = { parentId: string; parentName: string };

export function buildParentLookupFromTree(
  nodes: readonly NestableTreeNode[],
  childDepth: number
): Map<string, NestedTreemapParent> {
  const map = new Map<string, NestedTreemapParent>();

  const visit = (
    node: NestableTreeNode,
    parent: NestableTreeNode | undefined
  ): void => {
    const depth = node.depth ?? 0;
    if (depth === childDepth && parent !== undefined) {
      map.set(nestedTreemapLookupKey(node.id), {
        parentId: nestedTreemapLookupKey(parent.id),
        parentName: parent.name,
      });
    }
    if (node.children) {
      for (const child of node.children) {
        visit(child, node);
      }
    }
  };

  for (const root of nodes) {
    visit(root, undefined);
  }

  return map;
}

const MAX_PARENT_GROUPS = 7;

export type FlatTreemapItem = {
  id: string;
  name: string;
  value: number;
  share?: number | null;
};

export type NestedTreemapParentTotal = {
  name: string;
  value: number;
  share: number | null;
};

export function buildNestedTreemapChildren(
  flatItems: readonly FlatTreemapItem[],
  parentLookup: Map<string, NestedTreemapParent>,
  parentTotals?: Map<string, NestedTreemapParentTotal>,
  options?: {
    omitStandaloneOther?: boolean;
  }
): TreeMapData['children'] {
  type Child = {
    name: string;
    value: number;
    id: string;
    share?: number | null;
  };
  type Group = {
    name: string;
    id: string;
    value: number;
    children: Child[];
  };

  const groupMap = new Map<string, Group>();
  const groups: Group[] = [];
  const standalone: Child[] = [];

  const isOtherBucket = (datum: { id: string; name: string }): boolean =>
    datum.id === '-1' || datum.name.toLowerCase() === 'other';

  let childRollup: Child | null = null;

  for (const item of flatItems) {
    const parent = parentLookup.get(nestedTreemapLookupKey(item.id));
    if (parent === undefined) {
      if (isOtherBucket(item)) {
        childRollup = {
          name: item.name,
          value: item.value,
          id: item.id,
          share: item.share,
        };
        continue;
      }
      standalone.push({
        name: item.name,
        value: item.value,
        id: item.id,
        share: item.share,
      });
      continue;
    }

    let group = groupMap.get(parent.parentId);
    if (group === undefined) {
      group = {
        name: parent.parentName,
        id: parent.parentId,
        value: 0,
        children: [],
      };
      groupMap.set(parent.parentId, group);
      groups.push(group);
    }
    group.children.push({
      name: item.name,
      value: item.value,
      id: item.id,
      share: item.share,
    });
    group.value += item.value;
  }

  for (const group of groups) {
    const trueTotal = parentTotals?.get(group.id);
    if (trueTotal !== undefined && trueTotal.value > group.value) {
      const childShareSum = group.children.reduce<number | null>(
        (sum, child) =>
          sum === null || typeof child.share !== 'number'
            ? null
            : sum + child.share,
        0
      );
      const otherShare =
        trueTotal.share !== null && childShareSum !== null
          ? Math.max(0, trueTotal.share - childShareSum)
          : undefined;
      group.children.push({
        name: 'Other',
        value: trueTotal.value - group.value,
        id: `other-${group.id}`,
        share: otherShare,
      });
      group.value = trueTotal.value;
    }
  }

  if (parentTotals !== undefined && parentTotals.size > 0) {
    let leftoverValue = 0;
    let leftoverShare: number | null = 0;
    const addLeftover = (value: number, share: number | null | undefined) => {
      leftoverValue += value;
      leftoverShare =
        leftoverShare === null || typeof share !== 'number'
          ? null
          : leftoverShare + share;
    };

    const candidates = [...parentTotals]
      .filter(
        ([parentId, total]) =>
          !groupMap.has(parentId) &&
          total.value > 0 &&
          total.name !== '' &&
          !isOtherBucket({ id: parentId, name: total.name })
      )
      .sort(([, a], [, b]) => b.value - a.value);
    for (const [parentId, total] of candidates) {
      if (groups.length >= MAX_PARENT_GROUPS) {
        addLeftover(total.value, total.share);
        continue;
      }
      groups.push({
        name: total.name,
        id: parentId,
        value: total.value,
        children: [
          {
            name: 'Other',
            value: total.value,
            id: `other-${parentId}`,
            share: total.share ?? undefined,
          },
        ],
      });
    }

    for (const [parentId, total] of parentTotals) {
      if (
        isOtherBucket({ id: parentId, name: total.name }) &&
        total.value > 0
      ) {
        addLeftover(total.value, total.share);
      }
    }

    // omitStandaloneOther (skills) intentionally hides the entire standalone
    // "Other", including parents dropped past the group cap.
    if (leftoverValue > 0 && !options?.omitStandaloneOther) {
      standalone.push({
        name: childRollup?.name ?? 'Other',
        value: leftoverValue,
        id: childRollup?.id ?? '-1',
        share: leftoverShare ?? undefined,
      });
    }
  } else if (childRollup !== null && !options?.omitStandaloneOther) {
    standalone.push(childRollup);
  }

  return [...groups, ...standalone];
}
